/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { Button, Space, Table, Input, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import ApiCaller from '../../services/api/general';
import { showSnackbar } from '../snackbar';
import AppPopOverMenu from '../appPopOverMenu';
// import './index.scss';
import './pagination.scss';
import SVGIcons from '../../assets/images/icons/svgIcons';
import ConfirmActionButton from '../buttons/Confirm-action-btn';


const { Search } = Input;

const ResizableTitle = ({ onResize, width, isSpacer, ...restProps }) => {
    if (isSpacer) {
        return (
            <th
                {...restProps}
                style={{
                    ...restProps.style,
                    flex: '1 1 auto',
                    minWidth: 0,
                    padding: 0,
                    border: 'none',
                }}
            />
        );
    }
    if (!width) {
        return <th {...restProps} />;
    }
    return (
        <th
            {...restProps}
            style={{
                ...restProps.style,
                flex: `0 0 ${width}px`,
                overflow: 'hidden',
                minWidth: 0,
                position: 'relative',
                userSelect: 'none',
                boxSizing: 'border-box',
            }}
        >
            {restProps.children}
            {/* Resize handle only for columns that have onResize — not the action column */}
            {onResize && (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <span
                    role="separator"
                    aria-label="Resize column"
                    style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'col-resize', zIndex: 1 }}
                    onMouseDown={(e) => {
                        e.preventDefault(); e.stopPropagation();
                        const startX = e.clientX;
                        const startWidth = width || 100;
                        const onMouseMove = (me) => onResize(Math.max(50, startWidth + me.clientX - startX));
                        const onMouseUp = () => {
                            document.removeEventListener('mousemove', onMouseMove);
                            document.removeEventListener('mouseup', onMouseUp);
                        };
                        document.addEventListener('mousemove', onMouseMove);
                        document.addEventListener('mouseup', onMouseUp);
                    }}
                />
            )}
        </th>
    );
};

const AppTable = (props) => {
    const {
        columns = [],
        dataURL = '',
        queryParams = {},
        allowSearch = true,
        isRefreshData = false,
        onRefreshSuccess = () => null,
        actionColumnSettings = {},
        isDeleteActionEnabled = true,
        isEditActionEnabled = true,
        isViewActionEnabled = true,
        onEdit = null,
        onView = null,
        extraActions = null,
        pageSizeOptions = ['10', '20', '50'],
        apiSortFieldKey = 'sortField',
        apiSortOrderKey = 'sortOrder',
        rowClassName = () => '',
    } = props;

    const storageKey = `colWidths_${dataURL.split('?')[0]}`;
    const [colWidths, setColWidths] = useState(
        () => ({ ...(JSON.parse(localStorage.getItem(storageKey)) || {}) })
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            localStorage.setItem(storageKey, JSON.stringify(colWidths));
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colWidths]);

    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const [dataArr, setDataArr] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedId, setSelectedId] = useState('');

    const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
    const [pageSize, setPageSize] = useState(Number(searchParams.get('limit')) || 20);
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const [sortField, setSortField] = useState(searchParams.get('sortField') || 'createdAt');
    const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'desc');

    const navigate = useNavigate();

    useEffect(() => {
        getDataArr();
        if (isRefreshData) {
            onRefreshSuccess();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, searchTerm, isRefreshData, sortField, sortOrder]);

    const getDataArr = () => {
        if (dataURL) {
            setIsLoading(true);
            const apiCaller = new ApiCaller(dataURL);
            const params = {
                ...queryParams,
                limit: pageSize,
                page: currentPage,
            };
            if (searchTerm) params.searchTerm = searchTerm;
            if (sortField && sortOrder) {
                const sortOrderAs = sortOrder === 'ascend' ? 'asc' : 'desc';
                params.query = `sortBy=${sortField}:${sortOrderAs}`;
            }
            if (queryParams.dateRange && queryParams.dateRange[0] && queryParams.dateRange[1]) {
                params.startDate = dayjs(queryParams.dateRange[0]).format('YYYY-MM-DDTHH:mm:ss');
                params.endDate = dayjs(queryParams.dateRange[1]).format('YYYY-MM-DDTHH:mm:ss');
            }
            if (queryParams.state) params.state = queryParams.state;
            if (queryParams.status) params.status = queryParams.status;
            if (queryParams.agent) params.moduleId = queryParams.agent;
            if (queryParams.urgency) params.timelineUrgency = queryParams.urgency;
            if (queryParams.qualificationStatus) params.qualificationStatus = queryParams.qualificationStatus;

            apiCaller.getList(params)
                .then(({ data }) => {
                    if (data?.totalCount && data?.results && !data.results[0]?.id) {
                        const updatedData = data.results.reduce((acc, item) => {
                            acc.push({ ...item, id: item._id || item.id });
                            return acc;
                        }, []);
                        setDataArr(updatedData);
                    } else {
                        setDataArr(data.results);
                    }
                    setTotalCount(data.totalCount);
                })
                .catch(() => showSnackbar().failure('Something went wrong'))
                .finally(() => setIsLoading(false));
        }
    };

    const handleSearch = debounce((value) => {
        setSearchparams(value);
    }, 700);

    const setSearchparams = (value) => {
        setSearchTerm(value);
        setCurrentPage(1);
        const newParams = { page: 1, limit: pageSize, search: value };
        if (sortField) newParams[apiSortFieldKey] = sortField;
        if (sortOrder) newParams[apiSortOrderKey] = sortOrder;
        setSearchParams(newParams);
    };

    const deleteData = (id) => {
        if (id) {
            const apiUrl = dataURL.split('?')[0];
            const apiCaller = new ApiCaller(apiUrl);
            const apiCall = apiUrl === 'intents' ? apiCaller.deleteData(id) : apiCaller.customPatchData(id, { isDeleted: true });
            apiCall.then((d) => d.success ? showSnackbar().success('Resource deleted successfully') :
                showSnackbar().failure("Something went wrong!"))
                .catch(() => showSnackbar().failure("Something went wrong!"))
                .finally(() => {
                    setOpenDeleteDialog(false);
                    setSelectedId('');
                    getDataArr();
                });
        }
    };

    const onDeleteClick = (row) => {
        setOpenDeleteDialog(true);
        setSelectedId(row.id);
    };

    const handleTableChange = (pagination, filters, sorter) => {
        if (pagination.current !== currentPage) setCurrentPage(pagination.current);
        if (pagination.pageSize !== pageSize) setPageSize(pagination.pageSize);
        if (sorter && sorter.field) {
            setSortField(sorter.field);
            setSortOrder(sorter.order);
        } else {
            setSortField(null);
            setSortOrder(null);
        }
        const newParams = {
            page: pagination.current,
            limit: pagination.pageSize,
            search: searchTerm,
        };
        if (sorter && sorter.field && sorter.order) {
            newParams[apiSortFieldKey] = sorter.field;
            newParams[apiSortOrderKey] = sorter.order;
        }
        setSearchParams(newParams);
    };

    const renderColumns = () => {
        const actionColWidth = actionColumnSettings?.width || 70;
        const actionColumn = {
            dataIndex: 'action',
            key: 'action',
            width: actionColWidth,
            ...actionColumnSettings,
            onHeaderCell: () => ({ width: actionColWidth }),
            onCell: () => ({ flexWidth: actionColWidth }),
            render: (_, record) => (
                <Space className='action-table-btns' size={10}>
                    {isViewActionEnabled && <Button shape="circle" onClick={() => onView ? onView(record) : navigate(`view/${record.id}?page=${currentPage}&limit=${pageSize}`)}><SVGIcons.EyeOpen /></Button>}
                    {isEditActionEnabled && <Button shape="circle" onClick={() => onEdit ? onEdit(record) : navigate(`view/${record.id}?page=${currentPage}&limit=${pageSize}`)}><SVGIcons.EditIcon /></Button>}
                    {isDeleteActionEnabled && <>
                        <ConfirmActionButton
                            onConfirm={() => deleteData(record.id)}
                            buttonText={<SVGIcons.DeleteBlueIcon />}
                            okText='Yes'
                            shape='circle'
                            cancelText='Cancel'
                            title='Confirm Delete'
                            description='Are you sure you want to delete this resource?'
                        />
                    </>}
                    {typeof extraActions === 'function' && <AppPopOverMenu options={extraActions(record)} />}
                </Space>
            ),
        };

        const showActionColumn = isViewActionEnabled || isEditActionEnabled || isDeleteActionEnabled || typeof extraActions === 'function';

        const columnsWithResize = columns.map(col => {
            const colKey = col.dataIndex || col.key;
            const resolvedWidth = colWidths[colKey] || col.width || 100;
            return {
                ...col,
                width: resolvedWidth,
                sortOrder: col?.sorter ? sortField === col.dataIndex ? sortOrder : null : null,
                onHeaderCell: (c) => ({
                    width: c.width,
                    onResize: (newWidth) => setColWidths(prev => ({ ...prev, [colKey]: newWidth })),
                }),
                onCell: () => ({ flexWidth: resolvedWidth }),
            };
        });

        const spacerColumn = {
            key: '__spacer__',
            dataIndex: '__spacer__',
            title: '',
            render: () => null,
            onHeaderCell: () => ({ isSpacer: true }),
            onCell: () => ({ flexWidth: null }),
        };

        return [...columnsWithResize, ...(showActionColumn ? [actionColumn] : []), spacerColumn];
    };

    return (
        <>
            {allowSearch ? <Space className='search-box' style={{ marginBottom: 20 }}>
                <Search
                    placeholder="Search"
                    className='table-custom-search-input-wrp'
                    allowClear
                    onSearch={(v) => setSearchparams(v)}
                    enterButton
                    loading={isLoading}
                    defaultValue={searchTerm}
                    onChange={e => handleSearch(e.target.value)}
                    size="large"
                    style={{ width: 220 }}
                />
            </Space> : null}
            <Table
                style={{ width: '100%' }}
                scroll={{ x: true }}
                dataSource={dataArr}
                columns={renderColumns()}
                rowKey="id"
                className='app-table'
                loading={isLoading}
                rowClassName={rowClassName}
                components={{
                    table: ({ style, ...tableProps }) => (
                        // width:100% fills the container when columns are narrow.
                        // minWidth:fit-content expands the table to the column sum when
                        // columns overflow, so the scroll container activates correctly
                        // and the spacer collapses to zero instead of creating a white gap.
                        <table {...tableProps} style={{ ...style, display: 'block', width: '100%', minWidth: 'fit-content' }} />
                    ),
                    header: {
                        cell: ResizableTitle,
                        wrapper: ({ style, ...p }) => (
                            <thead {...p} style={{ ...style, display: 'block' }} />
                        ),
                        row: ({ style, ...p }) => (
                            <tr {...p} style={{ ...style, display: 'flex', width: '100%' }} />
                        ),
                    },
                    body: {
                        wrapper: ({ style, ...p }) => (
                            <tbody {...p} style={{ ...style, display: 'block' }} />
                        ),
                        row: ({ style, ...p }) => (
                            <tr {...p} style={{ ...style, display: 'flex', width: '100%' }} />
                        ),
                        cell: ({ flexWidth, style, ...p }) => (
                            <td
                                {...p}
                                style={{
                                    ...style,
                                    flex: flexWidth != null ? `0 0 ${flexWidth}px` : '1 1 auto',
                                    overflow: 'hidden',
                                    minWidth: 0,
                                    boxSizing: 'border-box',
                                }}
                            />
                        ),
                    },
                }}
                pagination={{
                    current: currentPage,
                    pageSize,
                    total: totalCount,
                    showSizeChanger: true,
                    pageSizeOptions,
                }}
                onChange={handleTableChange}
            />
            {!isLoading && dataArr.length > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', marginTop: 0, position: 'relative' }}>
                    <Tooltip title="Reset column widths">
                        <Button
                            shape="circle"
                            className="resetBtn"
                            size="small"
                            onClick={() => {
                                setColWidths({});
                                localStorage.removeItem(storageKey);
                            }}
                            icon={
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                    <path d="M3 3v5h5" />
                                </svg>
                            }
                        />
                    </Tooltip>
                </div>
            ) : null}
        </>
    );
};

AppTable.propTypes = {
    isRefreshData: PropTypes.bool,
    onRefreshSuccess: PropTypes.func,
    dataURL: PropTypes.string.isRequired,
    columns: PropTypes.array.isRequired,
    pageSizeOptions: PropTypes.array,
    actionColumnSettings: PropTypes.object,
    isDeleteActionEnabled: PropTypes.bool,
    isEditActionEnabled: PropTypes.bool,
    isViewActionEnabled: PropTypes.bool,
    onEdit: PropTypes.func,
    onView: PropTypes.func,
    extraActions: PropTypes.func,
    apiSortFieldKey: PropTypes.string,
    apiSortOrderKey: PropTypes.string,
    rowClassName: PropTypes.func,
};

export default AppTable;
